import type { Ref } from 'vue';

import { onUnmounted, ref, watch } from 'vue';

export interface FavoriteStarPulseTimings {
	firstPulseDelayMs?: number;
	firstPulseDurationMs?: number;
	gapBetweenPulsesMs?: number;
	secondPulseDurationMs?: number;
	celebrationFirstDelayMs?: number;
	celebrationGapBetweenPulsesMs?: number;
}

export interface UseFavoriteStarPulseOptions extends FavoriteStarPulseTimings {
	isEnabled: Ref<boolean>;
	isSelected: Ref<boolean>;
}

export interface UseFavoriteStarPulseResult {
	isMainPulsing: Ref<boolean>;
	isBackgroundPulsing: Ref<boolean>;
	start: () => void;
	clear: () => void;
}

const DEFAULTS: Required<FavoriteStarPulseTimings> = {
	firstPulseDelayMs: 150,
	firstPulseDurationMs: 250,
	gapBetweenPulsesMs: 800,
	secondPulseDurationMs: 350,
	celebrationFirstDelayMs: 0,
	celebrationGapBetweenPulsesMs: 100
};

/**
 * Calculate the total duration of the complete pulse animation sequence
 */
export function calculatePulseAnimationTotalDuration(
	timings: FavoriteStarPulseTimings = {}
): number {
	const times = { ...DEFAULTS, ...timings };
	return (
		times.firstPulseDelayMs +
		times.firstPulseDurationMs +
		times.gapBetweenPulsesMs +
		times.secondPulseDurationMs
	);
}

export function useFavoriteStarPulse(
	options: UseFavoriteStarPulseOptions
): UseFavoriteStarPulseResult {
	const times = { ...DEFAULTS, ...options };

	const isMainPulsing = ref(false);
	const isBackgroundPulsing = ref(false);
	const timeoutIds: number[] = [];
	const hasStartedDiscovery = ref(false);

	function schedule(cb: () => void, ms: number) {
		if (typeof window === 'undefined') return;
		const id = window.setTimeout(cb, ms);
		timeoutIds.push(id);
	}

	function clear() {
		for (const id of timeoutIds) clearTimeout(id);
		timeoutIds.length = 0;
		isMainPulsing.value = false;
		isBackgroundPulsing.value = false;
	}

	function runCelebrationPulseImmediate() {
		clear();
		// Main pulse immediately (no initial delay)
		isMainPulsing.value = true;
		schedule(() => {
			isMainPulsing.value = false;
		}, times.firstPulseDurationMs);

		// Background pulse after gap
		schedule(
			() => {
				isBackgroundPulsing.value = true;
				schedule(() => {
					isBackgroundPulsing.value = false;
				}, times.secondPulseDurationMs);
			},
			times.celebrationFirstDelayMs +
				times.firstPulseDurationMs +
				times.celebrationGapBetweenPulsesMs
		);
	}

	function start() {
		clear();
		if (!options.isEnabled.value || options.isSelected.value) return;

		// Discovery pulses (only for not selected)
		schedule(() => {
			isMainPulsing.value = true;
			schedule(() => {
				isMainPulsing.value = false;
			}, times.firstPulseDurationMs);
		}, times.firstPulseDelayMs);

		const secondStart =
			times.firstPulseDelayMs +
			times.firstPulseDurationMs +
			times.gapBetweenPulsesMs;
		schedule(() => {
			isBackgroundPulsing.value = true;
			schedule(() => {
				isBackgroundPulsing.value = false;
			}, times.secondPulseDurationMs);
		}, secondStart);
	}

	watch(
		() => options.isEnabled.value,
		enabled => {
			// Only run discovery once when enabled the first time
			if (enabled && !hasStartedDiscovery.value) {
				hasStartedDiscovery.value = true;
				start();
			}
		},
		{ immediate: true }
	);

	watch(
		() => options.isSelected.value,
		(newSelected, oldSelected) => {
			// Favorite: immediate pulse
			if (newSelected && !oldSelected) {
				runCelebrationPulseImmediate();
				return;
			}
			// Desfavoritar: apenas limpar, não reiniciar discovery
			if (!newSelected && oldSelected) {
				clear();
			}
		}
	);

	onUnmounted(clear);

	return { isMainPulsing, isBackgroundPulsing, start, clear };
}
